import { useMemo } from "react";
import { Teammate } from "./mcpSlice";

interface ValidationResult {
  isValid: boolean;
  message: string;
}

/**
 * Hook to validate each step of the Datamate creation flow
 * @param currentStep - The current step number (1-based)
 * @param teammate - The current teammate data
 * @returns Validation result for the current step
 */
export const useDatamateStepValidation = (
  currentStep: number,
  teammate: Teammate | null
): ValidationResult => {
  return useMemo(() => {
    // Step 1: Define - Name is required
    if (currentStep === 1) {
      const name = teammate?.name;
      if (!name || (typeof name === "string" && name.trim() === "")) {
        return {
          isValid: false,
          message: "Please enter a name for your datamate",
        };
      }
      return { isValid: true, message: "" };
    }

    // Step 2: Integrations - At least one integration must be selected
    if (currentStep === 2) {
      const integrations = teammate?.integrations;
      const hasIntegrations =
        integrations && Array.isArray(integrations) && integrations.length > 0;

      if (!hasIntegrations) {
        return {
          isValid: false,
          message: "Please select at least one integration",
        };
      }
      return { isValid: true, message: "" };
    }

    // Step 3: Guardrails - No validation required
    if (currentStep === 3) {
      return { isValid: true, message: "" };
    }

    // Step 4: Overview - No validation required (last step)
    if (currentStep === 4) {
      return { isValid: true, message: "" };
    }

    // Default: valid
    return { isValid: true, message: "" };
  }, [currentStep, teammate]);
};
